/**
 * Company workspaces are always paid — no free company tier.
 * Use these helpers to decide switcher chips and the payment-required banner.
 */

export type CompanyBillingReason =
  | 'missing_billing'
  | 'subscription_failed'
  | 'expired'
  | null;

export type CompanyBillingAssessment = {
  /** Show payment warning banner while in this company context */
  needsPayment: boolean;
  reason: CompanyBillingReason;
  status: string;
  label: string;
  /** MUI Chip color token */
  color: 'default' | 'success' | 'warning' | 'error' | 'info';
  /** Hex for native badges */
  colorHex: string;
  /** Healthy active paid (or sponsored/demo) billing */
  hasActivePaidBilling: boolean;
  /**
   * Whether the company may be selected in the switcher.
   * Payment issues stay selectable so the user can open Upgrade and fix billing.
   */
  isSelectable: boolean;
};

const COLOR_HEX = {
  default: '#64748b',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#6366f1',
} as const;

const FAILED_STATUSES = new Set([
  'cancelled',
  'past_due',
  'incomplete_expired',
  'unpaid',
  'failed',
  'payment_failed',
  'incomplete',
]);

const HEALTHY_STATUSES = new Set([
  'active',
  'subscribed',
  'trialing',
]);

type BillingEntry = {
  isActive?: boolean;
  status?: string;
  endDate?: string | Date | number;
  [key: string]: unknown;
};

/** Prefer an active billing row; otherwise the last known platform entry (failed/expired). */
export function getCompanyBillingEntry(
  billingInfo: unknown,
): BillingEntry | null {
  if (!billingInfo || typeof billingInfo !== 'object') return null;
  const values = Object.values(billingInfo as Record<string, unknown>).filter(
    (v) => v && typeof v === 'object',
  ) as BillingEntry[];
  if (values.length === 0) return null;
  const active = values.find((info) => info?.isActive);
  if (active) return active;
  return values[values.length - 1] ?? null;
}

function hasBillingObject(billingInfo: unknown): boolean {
  if (!billingInfo || typeof billingInfo !== 'object') return false;
  return Object.keys(billingInfo as object).length > 0;
}

function isEndDateExpired(endDate: BillingEntry['endDate']): boolean {
  if (endDate == null || endDate === '') return false;
  const d = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(d.getTime())) return false;
  return d < new Date();
}

function makeAssessment(
  partial: Omit<CompanyBillingAssessment, 'colorHex'> & {
    color: CompanyBillingAssessment['color'];
  },
): CompanyBillingAssessment {
  return {
    ...partial,
    colorHex: COLOR_HEX[partial.color],
  };
}

/**
 * Assess company billing. Companies are always paid — missing billing or a failed
 * last subscription requires payment.
 */
export function assessCompanyBilling(company: any): CompanyBillingAssessment {
  if (!company) {
    return makeAssessment({
      needsPayment: false,
      reason: null,
      status: 'none',
      label: '',
      color: 'default',
      hasActivePaidBilling: false,
      isSelectable: true,
    });
  }

  // Demo workspaces are exhibition sandboxes — no paid billing required
  if (company.demo) {
    return makeAssessment({
      needsPayment: false,
      reason: null,
      status: 'demo',
      label: 'Demo',
      color: 'default',
      hasActivePaidBilling: true,
      isSelectable: true,
    });
  }

  const isSponsored =
    company.isSponsored ||
    company.linkType === 'sponsored';

  if (isSponsored) {
    const softLocked = company.sponsorshipSoftLocked === true;
    return makeAssessment({
      needsPayment: false,
      reason: null,
      status: 'sponsored',
      label: softLocked ? 'Sponsored Expired' : 'Sponsored',
      color: softLocked ? 'error' : 'info',
      hasActivePaidBilling: !softLocked,
      isSelectable: true,
    });
  }

  if (!hasBillingObject(company.billing_info)) {
    return makeAssessment({
      needsPayment: true,
      reason: 'missing_billing',
      status: 'missing_billing',
      label: 'Payment required',
      color: 'error',
      hasActivePaidBilling: false,
      isSelectable: true,
    });
  }

  const billingInfo = getCompanyBillingEntry(company.billing_info);

  if (!billingInfo) {
    return makeAssessment({
      needsPayment: true,
      reason: 'missing_billing',
      status: 'missing_billing',
      label: 'Payment required',
      color: 'error',
      hasActivePaidBilling: false,
      isSelectable: true,
    });
  }

  if (billingInfo.status === 'sponsored') {
    return makeAssessment({
      needsPayment: false,
      reason: null,
      status: 'sponsored',
      label: 'Sponsored',
      color: 'info',
      hasActivePaidBilling: true,
      isSelectable: true,
    });
  }

  const status = (billingInfo.status || 'inactive').toLowerCase();
  const expired = isEndDateExpired(billingInfo.endDate);

  if (expired) {
    return makeAssessment({
      needsPayment: true,
      reason: 'expired',
      status: 'expired',
      label: 'Expired',
      color: 'error',
      hasActivePaidBilling: false,
      isSelectable: true,
    });
  }

  if (FAILED_STATUSES.has(status)) {
    return makeAssessment({
      needsPayment: true,
      reason: 'subscription_failed',
      status,
      label:
        status === 'past_due'
          ? 'Past Due'
          : status === 'incomplete' || status === 'incomplete_expired'
            ? 'Incomplete'
            : status === 'cancelled'
              ? 'Cancelled'
              : 'Payment failed',
      color: 'error',
      hasActivePaidBilling: false,
      isSelectable: true,
    });
  }

  if (HEALTHY_STATUSES.has(status) && billingInfo.isActive !== false) {
    return makeAssessment({
      needsPayment: false,
      reason: null,
      status: status === 'trialing' ? 'trialing' : 'active',
      label: status === 'trialing' ? 'Trial' : 'Premium',
      color: status === 'trialing' ? 'warning' : 'success',
      hasActivePaidBilling: true,
      isSelectable: true,
    });
  }

  // Present but inactive / unknown — treat as unpaid company
  return makeAssessment({
    needsPayment: true,
    reason: 'subscription_failed',
    status: status || 'inactive',
    label: 'Payment required',
    color: 'error',
    hasActivePaidBilling: false,
    isSelectable: true,
  });
}

/** @deprecated Prefer assessCompanyBilling — kept for call-site clarity */
export function isCompanySubscriptionValid(company: any): boolean {
  return assessCompanyBilling(company).isSelectable;
}

export function getCompanySubscriptionStatus(company: any) {
  const a = assessCompanyBilling(company);
  return {
    status: a.status,
    label: a.label,
    color: a.color,
    colorHex: a.colorHex,
    isValid: a.hasActivePaidBilling,
    needsPayment: a.needsPayment,
    reason: a.reason,
  };
}

export function companyPaymentBannerMessageKey(
  reason: CompanyBillingReason,
): 'companyPaymentMissingBilling' | 'companyPaymentSubscriptionFailed' | 'companyPaymentExpired' {
  if (reason === 'expired') return 'companyPaymentExpired';
  if (reason === 'subscription_failed') return 'companyPaymentSubscriptionFailed';
  return 'companyPaymentMissingBilling';
}
